import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { CALL_INS_QUERY_KEY_ROOT, callInCancelMessage } from "../utils/callIns";
import type { CalledOffShift, CalledOffShiftResponse, GetLimitedCallOffs } from "../utils/models";

/**
 * Page size every "all of this department's / this nurse's call-ins" caller asks for. Shared because
 * the request parameters ARE the TanStack Query key: the profile page renders both the call-ins grid
 * and the schedule calendar, and they only collapse into a single request while their page sizes match.
 */
export const CALL_INS_PAGE_SIZE = 10000;

export function useGetLimitedCallOffs(request: GetLimitedCallOffs) {
	const queryClient = useQueryClient();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const key = useMemo(
		() => [
			CALL_INS_QUERY_KEY_ROOT,
			departmentId,
			request.nurseIds,
			request.page,
			request.pageSize,
			request.fromDate?.toFormat("yyyy-MM-dd"),
			request.toDate?.toFormat("yyyy-MM-dd"),
		],
		[departmentId, request.nurseIds, request.page, request.pageSize, request.fromDate, request.toDate],
	);
	const baseKey = useMemo(
		() => [
			CALL_INS_QUERY_KEY_ROOT,
			departmentId,
			request.nurseIds,
			request.pageSize,
			request.fromDate?.toFormat("yyyy-MM-dd"),
			request.toDate?.toFormat("yyyy-MM-dd"),
		],
		[departmentId, request.nurseIds, request.pageSize, request.fromDate, request.toDate],
	);
	const baseEmptyResponse: CalledOffShiftResponse = useMemo(
		() => ({
			hasMore: false,
			results: [],
		}),
		[],
	);

	// Subscribe to the combined list; disabled => no fetching, but still observes cache updates
	const combinedQuery = useQuery<CalledOffShiftResponse>({
		queryKey: baseKey,
		enabled: false,
		initialData: baseEmptyResponse,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: async () => {
			return baseEmptyResponse;
		}, // never actually used
	});

	const { isSuccess, data, ...query } = useQuery<CalledOffShiftResponse, Error>({
		queryKey: key,
		queryFn: async () => {
			if (!departmentId || !request.page || !request.pageSize) {
				return baseEmptyResponse;
			}
			const result_unfiltered = (await backendService.getCallOffsWithLimit({ ...request, departmentId })).data;
			const result: CalledOffShiftResponse = {
				...result_unfiltered,
				results: result_unfiltered.results.filter((callOff) => callOff.message !== callInCancelMessage),
			};

			if (result) {
				//handle successful fetch
				const pageData = result;
				trackEvent("success_getting_limited_call_offs", {
					...request,
					length: pageData.results.length,
				});
				// Determine whether to append or reset the combined list
				const prevCombined = queryClient.getQueryData<CalledOffShiftResponse>(baseKey) ?? baseEmptyResponse;
				const prevPageCount = Math.ceil(prevCombined.results.length / request.pageSize);

				if (request.page === 1) {
					queryClient.setQueryData<CalledOffShiftResponse>(baseKey, pageData);
				} else if (request.page > prevPageCount) {
					const seen = new Set(prevCombined.results.map((i) => i.id));
					const merged = prevCombined.results.concat(pageData.results.filter((i) => !seen.has(i.id)));
					queryClient.setQueryData<CalledOffShiftResponse>(baseKey, {
						...pageData,
						results: merged,
					});
				}
			}
			return result;
		},
		enabled:
			(request.enabled ?? true) &&
			!!departmentId &&
			request.page > 0 &&
			request.pageSize > 0 &&
			(!request.nurseIds || request.nurseIds.filter((val) => val > 0).length > 0),
		throwOnError: (error) => {
			trackErrorEvent("get_limited_call_offs_error", error, { ...request });
			return false;
		},
	});

	const updateCallOff = useCallback(
		(id: string, updatedFields: Partial<CalledOffShift>) => {
			queryClient.setQueryData<CalledOffShiftResponse>(baseKey, (oldData) => {
				if (!oldData) return oldData;
				const results = oldData.results
					.map((item) => (item.id === id ? { ...item, ...updatedFields } : item))
					.filter((item) => item.message !== callInCancelMessage);

				return {
					...oldData,
					results,
				} as CalledOffShiftResponse;
			});
		},
		[queryClient, baseKey],
	);

	return {
		...query,
		combinedData: combinedQuery.data,
		updateCallOff,
	};
}
